import type { ChangeEvent, FC, FormEvent, ReactNode } from 'react'

import { useRef, useState } from 'react'

import { cn } from '@/lib/utils'
import { gsap } from '@/lib/gsap'
import { useIsomorphicLayoutEffect } from '@/hooks/use-isomorphic-layout-effect'
import { contact } from '@/constants/site'

const PROJECT_TYPES = ['Portrait', 'Wedding', 'Editorial', 'Architecture', 'Something else']

const BUDGETS = ['Under £1,000', '£1,000 to £2,500', '£2,500 to £5,000', '£5,000 and above', 'Not sure yet']

const HEARD_VIA = ['Instagram', 'A recommendation', 'Search', 'A magazine', 'Somewhere else']

type Values = {
    name: string
    email: string
    phone: string
    projectType: string
    eventDate: string
    location: string
    budget: string
    message: string
    heardVia: string
    company: string
}

const EMPTY: Values = {
    name: '',
    email: '',
    phone: '',
    projectType: '',
    eventDate: '',
    location: '',
    budget: '',
    message: '',
    heardVia: '',
    company: ''
}

const fieldClass = 'w-full border-0 border-b border-border bg-transparent px-0 py-3 text-base text-foreground transition-colors duration-500 placeholder:text-muted-foreground/70 focus:border-foreground focus:outline-none focus:ring-0'

export const InquiryForm: FC = (): ReactNode => {
    const [values, setValues] = useState<Values>(EMPTY)
    const [errors, setErrors] = useState<Record<string, string>>({})
    const [status, setStatus] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle')
    const [failure, setFailure] = useState('')

    const root = useRef<HTMLDivElement>(null)

    useIsomorphicLayoutEffect(() => {
        const element = root.current

        if (!element) {
            return
        }

        const context = gsap.context(() => {
            gsap.set(element.querySelectorAll('[data-reveal]'), { visibility: 'visible' })

            if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
                return
            }

            gsap.fromTo(
                element.querySelectorAll('[data-field]'),
                { y: 26, opacity: 0 },
                {
                    y: 0,
                    opacity: 1,
                    duration: 0.85,
                    ease: 'power3.out',
                    stagger: 0.07,
                    scrollTrigger: {
                        trigger: element,
                        start: 'top 78%',
                        once: true
                    }
                }
            )
        }, element)

        return () => context.revert()
    }, [])

    const update = (key: keyof Values) => (
        event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
    ) => {
        setValues((previous) => ({ ...previous, [key]: event.target.value }))
        setErrors((previous) => {
            if (!previous[key]) {
                return previous
            }

            const next = { ...previous }

            delete next[key]

            return next
        })
    }

    const onSubmit = async (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault()

        setStatus('sending')
        setErrors({})
        setFailure('')

        try {
            const response = await fetch('/api/inquiries', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(values)
            })

            const payload = await response.json()

            if (!response.ok) {
                setErrors(payload.fields ?? {})
                setFailure(payload.error ?? 'Something went wrong. Please try again.')
                setStatus('error')

                return
            }

            setValues(EMPTY)
            setStatus('sent')
        } catch {
            setFailure('The enquiry could not be sent. Please check your connection, or email the studio directly.')
            setStatus('error')
        }
    }

    if (status === 'sent') {
        return (
            <div
                ref={root}
                className='flex min-h-[26rem] flex-col justify-center border-t border-border py-16'
            >
                <p className='label mb-6 text-[color:var(--gold-ink)]'>Enquiry received</p>

                <h3 className='display-md mb-6 max-w-2xl text-foreground'>
                    Thank you. That has landed in the studio inbox.
                </h3>

                <p className='mb-10 max-w-md text-sm leading-relaxed text-muted-foreground'>
                    {contact.response}. If your date is close and you would rather not wait, the
                    studio phone is the quicker route.
                </p>

                <div className='flex flex-wrap gap-8'>
                    <a href={`tel:${contact.phone.replace(/\s/g, '')}`} className='link-wipe text-sm text-foreground'>
                        {contact.phone}
                    </a>

                    <button
                        type='button'
                        onClick={() => setStatus('idle')}
                        className='label text-muted-foreground transition-colors duration-500 hover:text-foreground'
                    >
                        Send another
                    </button>
                </div>
            </div>
        )
    }

    return (
        <div ref={root}>
            <form onSubmit={onSubmit} noValidate className='border-t border-border pt-10'>
                <div className='grid gap-x-10 gap-y-8 md:grid-cols-2'>
                    <div data-field>
                        <label htmlFor='name' className='label mb-2 block text-muted-foreground'>
                            Your name <span className='text-[color:var(--gold-ink)]'>*</span>
                        </label>

                        <input
                            id='name'
                            name='name'
                            type='text'
                            autoComplete='name'
                            required
                            value={values.name}
                            onChange={update('name')}
                            aria-invalid={Boolean(errors.name)}
                            aria-describedby={errors.name ? 'name-error' : undefined}
                            className={cn(fieldClass, errors.name && 'border-destructive')}
                            placeholder='Rowan Vale'
                        />

                        {errors.name && (
                            <p id='name-error' role='alert' className='mt-2 text-xs text-destructive'>
                                {errors.name}
                            </p>
                        )}
                    </div>

                    <div data-field>
                        <label htmlFor='email' className='label mb-2 block text-muted-foreground'>
                            Email <span className='text-[color:var(--gold-ink)]'>*</span>
                        </label>

                        <input
                            id='email'
                            name='email'
                            type='email'
                            autoComplete='email'
                            required
                            value={values.email}
                            onChange={update('email')}
                            aria-invalid={Boolean(errors.email)}
                            aria-describedby={errors.email ? 'email-error' : undefined}
                            className={cn(fieldClass, errors.email && 'border-destructive')}
                            placeholder='you@example.com'
                        />

                        {errors.email && (
                            <p id='email-error' role='alert' className='mt-2 text-xs text-destructive'>
                                {errors.email}
                            </p>
                        )}
                    </div>

                    <div data-field>
                        <label htmlFor='phone' className='label mb-2 block text-muted-foreground'>
                            Phone
                        </label>

                        <input
                            id='phone'
                            name='phone'
                            type='tel'
                            autoComplete='tel'
                            value={values.phone}
                            onChange={update('phone')}
                            className={fieldClass}
                            placeholder='Optional'
                        />
                    </div>

                    <div data-field>
                        <label htmlFor='projectType' className='label mb-2 block text-muted-foreground'>
                            What kind of work <span className='text-[color:var(--gold-ink)]'>*</span>
                        </label>

                        <select
                            id='projectType'
                            name='projectType'
                            required
                            value={values.projectType}
                            onChange={update('projectType')}
                            aria-invalid={Boolean(errors.projectType)}
                            aria-describedby={errors.projectType ? 'projectType-error' : undefined}
                            className={cn(fieldClass, 'cursor-pointer', errors.projectType && 'border-destructive')}
                        >
                            <option value='' className='bg-background'>
                                Choose one
                            </option>

                            {PROJECT_TYPES.map((type) => (
                                <option key={type} value={type} className='bg-background'>
                                    {type}
                                </option>
                            ))}
                        </select>

                        {errors.projectType && (
                            <p id='projectType-error' role='alert' className='mt-2 text-xs text-destructive'>
                                {errors.projectType}
                            </p>
                        )}
                    </div>

                    <div data-field>
                        <label htmlFor='eventDate' className='label mb-2 block text-muted-foreground'>
                            Date, if you have one
                        </label>

                        <input
                            id='eventDate'
                            name='eventDate'
                            type='text'
                            value={values.eventDate}
                            onChange={update('eventDate')}
                            className={fieldClass}
                            placeholder='June 2026, or a rough window'
                        />
                    </div>

                    <div data-field>
                        <label htmlFor='location' className='label mb-2 block text-muted-foreground'>
                            Where
                        </label>

                        <input
                            id='location'
                            name='location'
                            type='text'
                            value={values.location}
                            onChange={update('location')}
                            className={fieldClass}
                            placeholder='City, venue, or somewhere vague'
                        />
                    </div>

                    <div data-field>
                        <label htmlFor='budget' className='label mb-2 block text-muted-foreground'>
                            Budget
                        </label>

                        <select
                            id='budget'
                            name='budget'
                            value={values.budget}
                            onChange={update('budget')}
                            className={cn(fieldClass, 'cursor-pointer')}
                        >
                            <option value='' className='bg-background'>
                                Rather not say
                            </option>

                            {BUDGETS.map((band) => (
                                <option key={band} value={band} className='bg-background'>
                                    {band}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div data-field>
                        <label htmlFor='heardVia' className='label mb-2 block text-muted-foreground'>
                            How you found me
                        </label>

                        <select
                            id='heardVia'
                            name='heardVia'
                            value={values.heardVia}
                            onChange={update('heardVia')}
                            className={cn(fieldClass, 'cursor-pointer')}
                        >
                            <option value='' className='bg-background'>
                                Choose one
                            </option>

                            {HEARD_VIA.map((source) => (
                                <option key={source} value={source} className='bg-background'>
                                    {source}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div data-field className='md:col-span-2'>
                        <label htmlFor='message' className='label mb-2 block text-muted-foreground'>
                            Tell me about it <span className='text-[color:var(--gold-ink)]'>*</span>
                        </label>

                        <textarea
                            id='message'
                            name='message'
                            required
                            rows={5}
                            value={values.message}
                            onChange={update('message')}
                            aria-invalid={Boolean(errors.message)}
                            aria-describedby={errors.message ? 'message-error' : 'message-hint'}
                            className={cn(fieldClass, 'resize-y', errors.message && 'border-destructive')}
                            placeholder='Who is in the pictures, what they are for, and anything you already know about the day.'
                        />

                        {errors.message ? (
                            <p id='message-error' role='alert' className='mt-2 text-xs text-destructive'>
                                {errors.message}
                            </p>
                        ) : (
                            <p id='message-hint' className='mt-2 text-xs text-muted-foreground'>
                                There is no wrong amount of detail here.
                            </p>
                        )}
                    </div>
                </div>

                {/* Honeypot, visually and programmatically hidden from people. */}
                <div aria-hidden='true' className='absolute left-[-9999px] h-px w-px overflow-hidden'>
                    <label htmlFor='company'>Company</label>
                    <input
                        id='company'
                        name='company'
                        type='text'
                        tabIndex={-1}
                        autoComplete='off'
                        value={values.company}
                        onChange={update('company')}
                    />
                </div>

                <div data-field className='mt-12 flex flex-wrap items-center gap-8'>
                    <button
                        type='submit'
                        disabled={status === 'sending'}
                        data-cursor
                        data-cursor-label='Send'
                        className='group relative inline-flex items-center gap-4 overflow-hidden border border-foreground/30 px-8 py-4 text-sm transition-colors duration-500 hover:border-[color:var(--gold-ink)] disabled:cursor-wait disabled:opacity-60'
                    >
                        <span className='absolute inset-0 -z-10 translate-y-full bg-accent transition-transform duration-700 ease-[cubic-bezier(0.16,1,0.3,1)] group-hover:translate-y-0' />

                        <span className='transition-colors duration-500 group-hover:text-[color:var(--gold-ink)]-foreground'>
                            {status === 'sending' ? 'Sending' : 'Send enquiry'}
                        </span>

                        <span
                            aria-hidden='true'
                            className={cn(
                                'transition-all duration-500 group-hover:translate-x-1 group-hover:text-[color:var(--gold-ink)]-foreground',
                                status === 'sending' && 'animate-pulse'
                            )}
                        >
                            &rarr;
                        </span>
                    </button>

                    <p className='max-w-xs text-xs leading-relaxed text-muted-foreground'>
                        {contact.response}. Your details are never passed on to anyone.
                    </p>
                </div>

                {status === 'error' && failure && (
                    <p role='alert' className='mt-8 border-l-2 border-destructive pl-4 text-sm text-destructive'>
                        {failure}
                    </p>
                )}
            </form>
        </div>
    )
}
